// src/components/tests/PdfAnnotationViewer.jsx
// PDF annotation module: embedded viewer + pen/highlighter annotations + autosave + export flattened PDF

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { PDFDocument, rgb } from 'pdf-lib';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Box,
  Stack,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Card,
  CardContent,
  Chip,
  TextField,
  LinearProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Close as CloseIcon,
  Edit as PenIcon,
  Highlight as HighlighterIcon,
  Backspace as EraserIcon,
  TextFields as TextIcon,
  PictureAsPdf as ExportIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import { PDF_ANNOTATIONS } from '../../config/Config';
import { TeacherNoteEditor, RubricTripleEditor, CorrectAnswerEditor } from './QuestionGradingEditors';
import MathRenderer from '../common/MathRenderer';
import AiReviewButton from '../../features/aiWorksheetReview/AiReviewButton';
import AiReviewPanel from '../../features/aiWorksheetReview/AiReviewPanel';
import AiRecommendationOverlay from '../../features/aiWorksheetReview/AiRecommendationOverlay';
import useAiWorksheetReview from '../../features/aiWorksheetReview/useAiWorksheetReview';
import { groupAiRecommendationsByPage } from '../../features/aiWorksheetReview/adapter';

// Configure PDF.js worker (CDN - works without build config)
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const TOOLS = { pen: 'pen', highlighter: 'highlighter', eraser: 'eraser', text: 'text' };
const ERASER_HIT_THRESHOLD = 0.1; // normalized distance for stroke hit detection when erasing
const ERASER_CURSOR = `url("data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><rect x="4" y="10" width="16" height="10" rx="2" fill="%239e9e9e" stroke="%23424242" stroke-width="2"/></svg>')}") 12 12, auto`;
const PEN_COLORS = ['#000000', '#e53935', '#1e88e5', '#43a047'];
const HIGHLIGHTER_COLORS = ['#fff59d', '#ffcc80', '#ce93d8', '#80deea'];
const DEFAULT_DIALOG_SIZE = { width: 1100, height: 720 };
const DIALOG_SIZE_WITH_PANEL = { width: 1600, height: 720 };
const MIN_DIALOG_SIZE = { width: 500, height: 400 };
const RESIZE_HANDLE_SIZE = 8;

function rubricOverrideKey(row) {
  if (row.isSub && row.subStudentAnswerId) return `sa:${row.subStudentAnswerId}`;
  if (!row.isSub) return `q:${row.id}`;
  return null;
}

function binaryRubricSet(v) {
  if (v == null || v === '') return false;
  if (v === 'correct' || v === 'incorrect') return true;
  if (v === '50' || v === '100') return true;
  return false;
}

/** Grading row considered “marked” if accuracy, marks, or binary rubric fields are set (props + local PATCH overlay). */
function isGradingRowMarked(row, questions, rubricOverrides) {
  const k = rubricOverrideKey(row);
  const ov = k ? rubricOverrides[k] : null;
  if (row.isSub) {
    const mp = ov?.mark_percentage !== undefined ? ov.mark_percentage : row.mark_percentage_sub;
    const rn = ov?.rubric_notation !== undefined ? ov.rubric_notation : row.rubric_notation;
    const rs = ov?.rubric_steps !== undefined ? ov.rubric_steps : row.rubric_steps;
    const mo = ov?.marks_obtained !== undefined ? ov.marks_obtained : row.marks_obtained;
    return (
      (mp != null && String(mp).trim() !== '') ||
      (mo != null && mo !== '') ||
      binaryRubricSet(rn) ||
      binaryRubricSet(rs)
    );
  }
  const q = questions.find((x) => x.id === row.id);
  const mp = ov?.mark_percentage !== undefined ? ov.mark_percentage : q?.mark_percentage;
  const rn = ov?.rubric_notation !== undefined ? ov.rubric_notation : q?.rubric_notation;
  const rs = ov?.rubric_steps !== undefined ? ov.rubric_steps : q?.rubric_steps;
  return (
    (mp != null && String(mp).trim() !== '') ||
    binaryRubricSet(rn) ||
    binaryRubricSet(rs)
  );
}

const PdfAnnotationViewer = ({
  open,
  onClose,
  uploadId,
  filename,
  role = 'student',
  attemptId,
  questions = [],
  revisionData = [],
  readOnlyAnnotations = false,
  currentUserId = null,
  gradingDone = true,
  /** 'student' = StudentUpload id; 'teacher' = TeacherUpload id (e.g. roster worksheet). */
  uploadSource = 'student',
}) => {
  const [pdfData, setPdfData] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [annotations, setAnnotations] = useState({});
  const [annotationId, setAnnotationId] = useState(null);
  const [tool, setTool] = useState(TOOLS.pen);
  const [color, setColor] = useState(PEN_COLORS[0]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [exporting, setExporting] = useState(false);
  const [currentStroke, setCurrentStroke] = useState(null);
  const [annotationFilter, setAnnotationFilter] = useState('all');
  const [maximized, setMaximized] = useState(false);
  const [dialogSize, setDialogSize] = useState(DEFAULT_DIALOG_SIZE);
  const [resizingHandle, setResizingHandle] = useState(null);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const dialogPaperRef = useRef(null);
  const resizeStateRef = useRef({ startX: 0, startY: 0, startW: 0, startH: 0 });
  const dragStartRef = useRef({ clientX: 0, clientY: 0, offsetX: 0, offsetY: 0 });
  const canvasRefs = useRef({});
  const saveTimeoutRef = useRef(null);
  const pdfUrlRef = useRef(null);
  const prevUploadIdRef = useRef(null);
  const pdfLoadedForUploadRef = useRef(null);
  const redrawScheduledRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const pdfContainerRef = useRef(null);
  const [pdfContainerWidth, setPdfContainerWidth] = useState(null);
  const [mobileGradeDialogRow, setMobileGradeDialogRow] = useState(null);
  /** Latest rubric PATCH payloads keyed sa:{subAnswerId} or q:{questionId}; merges with `questions` until parent refetches. */
  const [rubricOverrides, setRubricOverrides] = useState({});
  const [textInputAnchor, setTextInputAnchor] = useState(null);
  const [textInputValue, setTextInputValue] = useState('');
  const userHasEditedRef = useRef(false);
  const eraserActiveRef = useRef(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTeacher = role === 'teacher';
  // Graded by Teacher pane: show for teachers always; for students only when teacher has marked grading done
  const showQuestionsPanel = attemptId && Array.isArray(questions) && questions.length > 0 && (isTeacher || (readOnlyAnnotations && gradingDone));
  const canAnnotate = (isTeacher || role === 'student') && !readOnlyAnnotations;
  const aiReview = useAiWorksheetReview({
    open,
    uploadId,
    uploadSource,
    enabledForRole: isTeacher && !readOnlyAnnotations,
  });

  useEffect(() => {
    if (!open) setRubricOverrides({});
  }, [open]);

  const handleRubricPersisted = useCallback((payload) => {
    const key = payload.subAnswerId != null ? `sa:${payload.subAnswerId}` : `q:${payload.questionId}`;
    setRubricOverrides((prev) => ({
      ...prev,
      [key]: {
        mark_percentage: payload.mark_percentage,
        rubric_notation: payload.rubric_notation,
        rubric_steps: payload.rubric_steps,
      },
    }));
  }, []);

  const gradingSaveCallbacksRef = useRef([]);
  const registerGradingSave = useCallback((flushFn) => {
    gradingSaveCallbacksRef.current.push(flushFn);
    return () => {
      gradingSaveCallbacksRef.current = gradingSaveCallbacksRef.current.filter((f) => f !== flushFn);
    };
  }, []);

  const flushGradingCallbacks = useCallback(async () => {
    const fns = [...gradingSaveCallbacksRef.current];
    await Promise.all(
      fns.map((f) =>
        Promise.resolve()
          .then(() => f())
          .catch((e) => console.error('Grading flush failed', e))
      )
    );
  }, []);

  const handleRequestClose = useCallback(
    async (event, reason) => {
      if (isTeacher && showQuestionsPanel) {
        await flushGradingCallbacks();
      }
      if (typeof onClose === 'function') {
        onClose(event, reason);
      }
    },
    [isTeacher, showQuestionsPanel, flushGradingCallbacks, onClose]
  );

  const closeMobileGradeDialog = useCallback(
    async () => {
      if (isTeacher && showQuestionsPanel) {
        await flushGradingCallbacks();
      }
      setMobileGradeDialogRow(null);
    },
    [isTeacher, showQuestionsPanel, flushGradingCallbacks]
  );

  const deferredRubricSave = isTeacher && showQuestionsPanel ? registerGradingSave : undefined;

  const showSnack = (msg, severity = 'info') =>
    setSnackbar({ open: true, message: msg, severity });


  const addStrokeToPage = useCallback(
    (pageIndex, stroke) => {
      setAnnotations((prev) => {
        const pageKey = String(pageIndex);
        const pageData = prev[pageKey] || { strokes: [] };
        const strokes = [...(pageData.strokes || []), stroke];
        return { ...prev, [pageKey]: { ...pageData, strokes } };
      });
      userHasEditedRef.current = true;
    },
    []
  );

  const removeStrokeFromPage = useCallback((pageIndex, strokeIndex) => {
    setAnnotations((prev) => {
      const pageKey = String(pageIndex);
      const pageData = prev[pageKey] || { strokes: [] };
      const strokes = [...(pageData.strokes || [])];
      if (strokeIndex >= 0 && strokeIndex < strokes.length) {
        strokes.splice(strokeIndex, 1);
        return { ...prev, [pageKey]: { ...pageData, strokes } };
      }
      return prev;
    });
    userHasEditedRef.current = true;
  }, []);

  const addTextToPage = useCallback((pageIndex, textObj) => {
    setAnnotations((prev) => {
      const pageKey = String(pageIndex);
      const pageData = prev[pageKey] || { strokes: [], texts: [] };
      const texts = [...(pageData.texts || []), textObj];
      return { ...prev, [pageKey]: { ...pageData, texts } };
    });
    userHasEditedRef.current = true;
  }, []);

  const removeTextFromPage = useCallback((pageIndex, textIndex) => {
    setAnnotations((prev) => {
      const pageKey = String(pageIndex);
      const pageData = prev[pageKey] || { strokes: [], texts: [] };
      const texts = [...(pageData.texts || [])];
      if (textIndex >= 0 && textIndex < texts.length) {
        texts.splice(textIndex, 1);
        return { ...prev, [pageKey]: { ...pageData, texts } };
      }
      return prev;
    });
    userHasEditedRef.current = true;
  }, []);

  const applyAiSuggestions = useCallback(() => {
    const groupedTexts = groupAiRecommendationsByPage(aiReview.recommendations, role);
    if (Object.keys(groupedTexts).length === 0) return;
    setAnnotations((prev) => {
      const next = { ...prev };
      Object.entries(groupedTexts).forEach(([pageKey, texts]) => {
        const pageData = next[pageKey] || { strokes: [], texts: [] };
        next[pageKey] = {
          ...pageData,
          texts: [...(pageData.texts || []), ...texts],
        };
      });
      return next;
    });
    userHasEditedRef.current = true;
    aiReview.clearRecommendations();
    showSnack('AI suggestions added as editable annotations', 'success');
  }, [aiReview, role]);

  const autosave = useCallback(async () => {
    if (Object.keys(annotations).length === 0) return;
    setSaving(true);
    try {
      if (annotationId) {
        await api.patch(`${PDF_ANNOTATIONS}${annotationId}/`, { data: annotations });
      } else {
        const body =
          uploadSource === 'teacher'
            ? { teacher_upload_id: uploadId, data: annotations }
            : { upload_id: uploadId, data: annotations };
        const res = await api.post(PDF_ANNOTATIONS, body);
        if (res.data?.id) setAnnotationId(res.data.id);
      }
    } catch (err) {
      showSnack(err?.response?.data?.detail || 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  }, [annotations, uploadId, annotationId, uploadSource]);

  useEffect(() => {
    if (!open || !annotations || Object.keys(annotations).length === 0) return;
    if (!userHasEditedRef.current) return;
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(autosave, 2000);
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, [annotations, open, autosave]);

  useEffect(() => {
    if (!open || !uploadId) return;
    const key = `${uploadSource}:${uploadId}`;
    if (prevUploadIdRef.current !== key) {
      prevUploadIdRef.current = key;
      userHasEditedRef.current = false;
      setAnnotations({});
      setAnnotationId(null);
    }
  }, [open, uploadId, uploadSource]);

  useEffect(() => {
    if (!open || !uploadId) return;
    const pdfKey = `${uploadSource}:${uploadId}`;
    if (pdfLoadedForUploadRef.current === pdfKey) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setPdfData(null);
    pdfLoadedForUploadRef.current = null;
    if (pdfUrlRef.current) {
      URL.revokeObjectURL(pdfUrlRef.current);
      pdfUrlRef.current = null;
    }
    const ac = new AbortController();
    const pdfUrl =
      uploadSource === 'teacher'
        ? `/teacher-uploads/${uploadId}/pdf-content/`
        : `/student-uploads/${uploadId}/pdf-content/`;
    api
      .get(pdfUrl, { responseType: 'arraybuffer', signal: ac.signal })
      .then((res) => {
        const buffer = res.data;
        const dataCopy = buffer.slice(0);
        const blob = new Blob([buffer], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        pdfUrlRef.current = url;
        pdfLoadedForUploadRef.current = pdfKey;
        setPdfData({ url, data: dataCopy });
        setLoading(false);
      })
      .catch((e) => {
        if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') return;
        showSnack(e?.response?.status === 401 ? 'Please log in again' : (e?.message || 'Failed to load PDF'), 'error');
        setLoading(false);
      });
    return () => {
      ac.abort();
    };
  }, [open, uploadId, uploadSource]);

  useEffect(() => {
    if (!open || !uploadId) return;
    const ac = new AbortController();
    const annParams =
      uploadSource === 'teacher' ? { teacher_upload_id: uploadId } : { upload_id: uploadId };
    api
      .get(PDF_ANNOTATIONS, { params: annParams, signal: ac.signal })
      .then((res) => {
        const items = Array.isArray(res.data) ? res.data : res.data?.results || [];
        const ann = items[0];
        if (ann?.data) {
          userHasEditedRef.current = false;
          setAnnotations(ann.data);
          setAnnotationId(ann.id);
        }
      })
      .catch((e) => {
        if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') return;
      });
    return () => ac.abort();
  }, [open, uploadId, uploadSource]);

  const handleDocumentLoadSuccess = ({ numPages: n }) => setNumPages(n);
  const documentFile = useMemo(
    () => (pdfData?.url ? { url: pdfData.url } : undefined),
    [pdfData?.url]
  );

  const getCoords = (e) => {
    const el = e.currentTarget;
    const rect = el.getBoundingClientRect();
    const clientX = e.clientX ?? e.touches?.[0]?.clientX ?? e.changedTouches?.[0]?.clientX ?? 0;
    const clientY = e.clientY ?? e.touches?.[0]?.clientY ?? e.changedTouches?.[0]?.clientY ?? 0;
    const w = rect.width || 1;
    const h = rect.height || 1;
    return {
      x: Math.max(0, Math.min(1, (clientX - rect.left) / w)),
      y: Math.max(0, Math.min(1, (clientY - rect.top) / h)),
    };
  };

  const distToSegment = (px, py, x1, y1, x2, y2) => {
    const dx = x2 - x1, dy = y2 - y1;
    const len = Math.hypot(dx, dy);
    if (len < 1e-9) return Math.hypot(px - x1, py - y1);
    const t = Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / (len * len)));
    const qx = x1 + t * dx, qy = y1 + t * dy;
    return Math.hypot(px - qx, py - qy);
  };

  const findStrokeIndexAtPoint = useCallback((pageIndex, x, y) => {
    const pageKey = String(pageIndex);
    const pageData = annotations[pageKey] || { strokes: [] };
    const strokes = [...(pageData.strokes || [])];
    for (let i = strokes.length - 1; i >= 0; i--) {
      const s = strokes[i];
      const strokeWidth = (s.width || 0.005) * 2;
      const threshold = Math.max(ERASER_HIT_THRESHOLD, strokeWidth);
      const pts = s.points || [];
      for (let j = 0; j < pts.length; j++) {
        const p = pts[j];
        if (!p || typeof p.x !== 'number' || typeof p.y !== 'number') continue;
        if (Math.hypot(p.x - x, p.y - y) < threshold) return i;
        if (j < pts.length - 1) {
          const p2 = pts[j + 1];
          if (p2 && typeof p2.x === 'number' && typeof p2.y === 'number' && distToSegment(x, y, p.x, p.y, p2.x, p2.y) < threshold) return i;
        }
      }
    }
    return -1;
  }, [annotations]);

  // Hit test for text annotations (normalized coords). Text box: top-left (x,y), approximate width = len * 0.6 * fontSize, height = fontSize.
  const findTextIndexAtPoint = useCallback((pageIndex, x, y) => {
    const pageKey = String(pageIndex);
    const pageData = annotations[pageKey] || { texts: [] };
    const texts = [...(pageData.texts || [])];
    const fontSizeNorm = 0.025;
    for (let i = texts.length - 1; i >= 0; i--) {
      const t = texts[i];
      const tx = typeof t.x === 'number' ? t.x : 0;
      const ty = typeof t.y === 'number' ? t.y : 0;
      const str = (t.text || '').toString();
      const f = (t.fontSize != null ? Number(t.fontSize) : fontSizeNorm) || fontSizeNorm;
      const w = Math.min(1 - tx, str.length * 0.6 * f);
      const h = f;
      if (x >= tx && x <= tx + w && y >= ty && y <= ty + h) return i;
    }
    return -1;
  }, [annotations]);

  const handlePointerDown = (e, pageIndex) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.pointerId !== undefined) {
      e.currentTarget.setPointerCapture(e.pointerId);
    }
    const { x, y } = getCoords(e);

    if (tool === TOOLS.eraser) {
      eraserActiveRef.current = true;
      const textIndex = findTextIndexAtPoint(pageIndex, x, y);
      if (textIndex >= 0) {
        removeTextFromPage(pageIndex, textIndex);
        return;
      }
      const strokeIndex = findStrokeIndexAtPoint(pageIndex, x, y);
      if (strokeIndex >= 0) {
        removeStrokeFromPage(pageIndex, strokeIndex);
      }
      return;
    }

    if (tool === TOOLS.text) {
      setTextInputAnchor({ pageIndex, x, y });
      return;
    }

    const stroke = {
      tool,
      color,
      width: tool === TOOLS.highlighter ? 0.03 : 0.005,
      points: [{ x, y }],
      role,
    };
    setCurrentStroke({ pageIndex, stroke });
  };

  const handlePointerMove = (e, pageIndex) => {
    if (tool === TOOLS.eraser && eraserActiveRef.current) {
      e.preventDefault();
      const { x, y } = getCoords(e);
      const textIndex = findTextIndexAtPoint(pageIndex, x, y);
      if (textIndex >= 0) {
        removeTextFromPage(pageIndex, textIndex);
        return;
      }
      const strokeIndex = findStrokeIndexAtPoint(pageIndex, x, y);
      if (strokeIndex >= 0) {
        removeStrokeFromPage(pageIndex, strokeIndex);
      }
      return;
    }
    if (!currentStroke || currentStroke.pageIndex !== pageIndex) return;
    e.preventDefault();
    const { x, y } = getCoords(e);
    setCurrentStroke((prev) => ({
      ...prev,
      stroke: {
        ...prev.stroke,
        points: [...prev.stroke.points, { x, y }],
      },
    }));
  };

  const handlePointerUp = (e, pageIndex) => {
    if (tool === TOOLS.eraser) {
      eraserActiveRef.current = false;
      return;
    }
    if (!currentStroke || currentStroke.pageIndex !== pageIndex) return;
    e.preventDefault();
    if (e.pointerId !== undefined) {
      try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) {}
    }
    addStrokeToPage(pageIndex, currentStroke.stroke);
    setCurrentStroke(null);
  };

  const handlePointerCancel = (e, pageIndex) => {
    if (tool === TOOLS.eraser) {
      eraserActiveRef.current = false;
    }
    if (currentStroke?.pageIndex === pageIndex) {
      setCurrentStroke(null);
    }
  };

  const handleExport = async () => {
    if (!pdfData?.data || numPages === 0) return;
    setExporting(true);
    try {
      const pdfDoc = await PDFDocument.load(pdfData.data);
      const pages = pdfDoc.getPages();

      for (let i = 0; i < pages.length; i++) {
        const page = pages[i];
        const { width, height } = page.getSize();

        if (annotations[String(i)]?.strokes?.length) {
          const strokes = annotations[String(i)].strokes;
          strokes.forEach((s) => {
            if (s.points.length < 2) return;
            const opacity = s.tool === TOOLS.highlighter ? 0.4 : 1;
            const c = hexToRgb(s.color);
            if (!c) return;
            const r = rgb(c.r, c.g, c.b);
            const thick = (s.width || 0.005) * Math.min(width, height);
            for (let j = 0; j < s.points.length - 1; j++) {
              const p1 = s.points[j];
              const p2 = s.points[j + 1];
              page.drawLine({
                start: { x: p1.x * width, y: (1 - p1.y) * height },
                end: { x: p2.x * width, y: (1 - p2.y) * height },
                color: r,
                thickness: thick,
                opacity,
              });
            }
          });
        }
        if (annotations[String(i)]?.texts?.length) {
          const texts = annotations[String(i)].texts;
          const minDim = Math.min(width, height);
          texts.forEach((t) => {
            const str = (t.text || '').toString();
            if (!str) return;
            const fontSizeNorm = t.fontSize != null ? Number(t.fontSize) : 0.025;
            const fontSizePt = fontSizeNorm * minDim * 0.6;
            const pdfX = (t.x ?? 0) * width;
            const pdfY = height - (t.y ?? 0) * height - fontSizePt;
            const c = hexToRgb(t.color || '#000000');
            if (!c) return;
            page.drawText(str, {
              x: pdfX,
              y: pdfY,
              size: fontSizePt,
              color: rgb(c.r, c.g, c.b),
            });
          });
        }
      }

      const blob = await pdfDoc.save();
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = (filename || 'annotated') + '.pdf';
      a.click();
      URL.revokeObjectURL(url);
      showSnack('PDF exported', 'success');
    } catch (err) {
      showSnack('Export failed', 'error');
    } finally {
      setExporting(false);
    }
  };

  const hexToRgb = (hex) => {
    const m = hex.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
    return m ? { r: parseInt(m[1], 16) / 255, g: parseInt(m[2], 16) / 255, b: parseInt(m[3], 16) / 255 } : null;
  };

  const attachTouchListeners = (canvasEl, idx) => {
    if (!canvasEl || !isTeacher) return;
    const opts = { passive: false };
    const down = (e) => {
      e.preventDefault();
      handlePointerDown(e, idx);
    };
    const move = (e) => {
      e.preventDefault();
      handlePointerMove(e, idx);
    };
    const up = (e) => {
      e.preventDefault();
      handlePointerUp(e, idx);
    };
    const cancel = (e) => handlePointerCancel(e, idx);
    canvasEl.addEventListener('touchstart', down, opts);
    canvasEl.addEventListener('touchmove', move, opts);
    canvasEl.addEventListener('touchend', up, opts);
    canvasEl.addEventListener('touchcancel', cancel, opts);
    return () => {
      canvasEl.removeEventListener('touchstart', down);
      canvasEl.removeEventListener('touchmove', move);
      canvasEl.removeEventListener('touchend', up);
      canvasEl.removeEventListener('touchcancel', cancel);
    };
  };

  const filterStrokesByRole = (strokes) => {
    if (annotationFilter === 'all') return strokes;
    return strokes.filter((s) => {
      const r = s.role || 'teacher';
      return annotationFilter === r;
    });
  };

  const filterTextsByRole = (texts) => {
    if (!Array.isArray(texts)) return [];
    if (annotationFilter === 'all') return texts;
    return texts.filter((t) => (t.role || 'teacher') === annotationFilter);
  };

  const getPageWidth = () => {
    if (typeof window === 'undefined') return 700;
    // Use measured container width whenever available (works for both maximized and small dialogs)
    if (pdfContainerWidth != null && pdfContainerWidth > 0) {
      return Math.max(400, Math.floor(pdfContainerWidth));
    }
    // Fallback: estimate based on dialog size or window width
    const panelWidth = showQuestionsPanel ? 480 : 0;
    const padding = maximized ? 40 : 80;
    const available = maximized 
      ? window.innerWidth - panelWidth - padding
      : (dialogSize.width - panelWidth - padding);
    return maximized ? Math.min(1200, Math.max(500, available)) : Math.min(700, Math.max(400, available));
  };

  const renderPageWithOverlay = (pageIndex) => {
    const pageKey = String(pageIndex);
    const pageData = annotations[pageKey] || { strokes: [] };
    let strokes = [...(pageData.strokes || [])];
    if (currentStroke?.pageIndex === pageIndex) {
      strokes.push(currentStroke.stroke);
    }
    strokes = filterStrokesByRole(strokes);

    return (
      <Box key={pageIndex} sx={{ position: 'relative', display: 'inline-block' }}>
        <Page
          pageNumber={pageIndex + 1}
          width={getPageWidth()}
          renderTextLayer={false}
          renderAnnotationLayer={false}
          onRenderSuccess={() => scheduleRedrawAll()}
        />
        <canvas
          ref={(el) => (canvasRefs.current[pageIndex] = el)}
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: '100%',
            height: '100%',
            zIndex: 1,
            cursor: tool === TOOLS.eraser ? ERASER_CURSOR : tool === TOOLS.text ? 'text' : 'crosshair',
            touchAction: canAnnotate ? 'none' : 'auto',
            pointerEvents: canAnnotate ? 'auto' : 'none',
            userSelect: 'none',
            WebkitUserSelect: 'none',
          }}
          onPointerDown={(e) => handlePointerDown(e, pageIndex)}
          onPointerMove={(e) => handlePointerMove(e, pageIndex)}
          onPointerUp={(e) => handlePointerUp(e, pageIndex)}
          onPointerLeave={(e) => handlePointerUp(e, pageIndex)}
          onPointerCancel={(e) => handlePointerCancel(e, pageIndex)}
        />
        <AiRecommendationOverlay
          pageIndex={pageIndex}
          recommendations={aiReview.recommendations}
          onChange={aiReview.updateRecommendation}
          onDelete={aiReview.deleteRecommendation}
          disabled={!canAnnotate}
        />
      </Box>
    );
  };

  const redrawCanvas = useCallback((pageIndex) => {
    const canvas = canvasRefs.current[pageIndex];
    if (!canvas) return false;
    const pageEl = canvas.parentElement?.querySelector('.react-pdf__Page');
    if (!pageEl) return false;
    const rect = pageEl.getBoundingClientRect();
    const pageKey = String(pageIndex);
    const pageData = annotations[pageKey] || { strokes: [], texts: [] };
    let strokes = [...(pageData.strokes || [])];
    if (currentStroke?.pageIndex === pageIndex) strokes.push(currentStroke.stroke);
    strokes = annotationFilter === 'all' ? strokes : strokes.filter((s) => (s.role || 'teacher') === annotationFilter);
    let texts = filterTextsByRole(pageData.texts || []);

    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    canvas.style.width = rect.width + 'px';
    canvas.style.height = rect.height + 'px';
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, rect.width, rect.height);

    const clamp = (v) => Math.max(0, Math.min(1, v));
    strokes.forEach((s) => {
      if (s.points.length < 2) return;
      ctx.beginPath();
      ctx.moveTo(clamp(s.points[0].x) * rect.width, clamp(s.points[0].y) * rect.height);
      for (let j = 1; j < s.points.length; j++) {
        ctx.lineTo(clamp(s.points[j].x) * rect.width, clamp(s.points[j].y) * rect.height);
      }
      ctx.strokeStyle = s.color;
      ctx.lineWidth = (s.width || 0.005) * Math.min(rect.width, rect.height);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.globalAlpha = s.tool === TOOLS.highlighter ? 0.4 : 1;
      ctx.stroke();
      ctx.globalAlpha = 1;
    });

    const fontSizeNorm = 0.025;
    texts.forEach((t) => {
      const str = (t.text || '').toString();
      if (!str) return;
      const tx = clamp(t.x ?? 0) * rect.width;
      const ty = clamp(t.y ?? 0) * rect.height;
      const f = (t.fontSize != null ? Number(t.fontSize) : fontSizeNorm) || fontSizeNorm;
      const fontSizePx = f * Math.min(rect.width, rect.height);
      ctx.font = `${fontSizePx}px sans-serif`;
      ctx.textBaseline = 'top';
      ctx.fillStyle = t.color || '#000000';
      ctx.fillText(str, tx, ty);
    });

    // Text insertion point indicator when Add text dialog is open
    if (textInputAnchor && textInputAnchor.pageIndex === pageIndex) {
      const px = clamp(textInputAnchor.x ?? 0) * rect.width;
      const py = clamp(textInputAnchor.y ?? 0) * rect.height;
      const cursorHeight = fontSizeNorm * Math.min(rect.width, rect.height);
      ctx.strokeStyle = color || '#1976d2';
      ctx.fillStyle = color || '#1976d2';
      ctx.lineWidth = 2;
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.moveTo(px, py);
      ctx.lineTo(px, py + cursorHeight);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(px, py, 4, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 1;
      ctx.stroke();
    }
    return true;
  }, [annotations, currentStroke, annotationFilter, textInputAnchor, color]);

  const scheduleRedrawAll = useCallback(() => {
    if (redrawScheduledRef.current) return;
    redrawScheduledRef.current = true;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        redrawScheduledRef.current = false;
        if (!pdfData || numPages === 0) return;
        for (let i = 0; i < numPages; i++) {
          if (!redrawCanvas(i)) {
            setTimeout(() => redrawCanvas(i), 50);
          }
        }
      });
    });
  }, [pdfData, numPages, redrawCanvas]);

  useEffect(() => {
    if (!pdfData || numPages === 0) return;
    scheduleRedrawAll();
  }, [annotations, currentStroke, annotationFilter, textInputAnchor, numPages, pdfData, scheduleRedrawAll]);

  useEffect(() => {
    if (!pdfData || numPages === 0) return;
    const container = document.querySelector('[class*="MuiDialogContent"]');
    if (!container) return;
    const ro = new ResizeObserver(() => scheduleRedrawAll());
    ro.observe(container);
    return () => ro.disconnect();
  }, [pdfData, numPages, scheduleRedrawAll]);

  useEffect(() => {
    const el = pdfContainerRef.current;
    if (!el || !open) return;
    const updateWidth = () => {
      const w = el.getBoundingClientRect().width;
      if (typeof w === 'number' && w > 0) setPdfContainerWidth(w);
    };
    const ro = new ResizeObserver(() => {
      updateWidth();
    });
    ro.observe(el);
    // Initial measurement
    updateWidth();
    return () => ro.disconnect();
  }, [open, showQuestionsPanel, maximized, dialogSize.width]);

  const handleResizeStart = useCallback((handle, e) => {
    e.preventDefault();
    e.stopPropagation();
    resizeStateRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startW: dialogSize.width,
      startH: dialogSize.height,
    };
    setResizingHandle(handle);
  }, [dialogSize.width, dialogSize.height]);

  useEffect(() => {
    if (!resizingHandle) return;
    const rs = resizeStateRef.current;
    const getCursor = () => {
      if (['n', 's'].includes(resizingHandle)) return 'ns-resize';
      if (['e', 'w'].includes(resizingHandle)) return 'ew-resize';
      if (['ne', 'sw'].includes(resizingHandle)) return 'nesw-resize';
      return 'nwse-resize';
    };
    const onMove = (e) => {
      const dx = e.clientX - rs.startX;
      const dy = e.clientY - rs.startY;
      let w = rs.startW;
      let h = rs.startH;
      if (resizingHandle.includes('e')) w += dx;
      if (resizingHandle.includes('w')) w -= dx;
      if (resizingHandle.includes('s')) h += dy;
      if (resizingHandle.includes('n')) h -= dy;
      setDialogSize({
        width: Math.max(MIN_DIALOG_SIZE.width, Math.min(window.innerWidth, w)),
        height: Math.max(MIN_DIALOG_SIZE.height, Math.min(window.innerHeight, h)),
      });
    };
    const onUp = () => {
      setResizingHandle(null);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.cursor = getCursor();
    document.body.style.userSelect = 'none';
    return onUp;
  }, [resizingHandle]);

  useEffect(() => {
    if (open && showQuestionsPanel && dialogSize.width === DEFAULT_DIALOG_SIZE.width) {
      setDialogSize(DIALOG_SIZE_WITH_PANEL);
    }
  }, [open, showQuestionsPanel, dialogSize.width]);

  const filteredRevisionData = useMemo(() => {
    if (!attemptId || !Array.isArray(revisionData)) return [];
    return revisionData.filter((r) => r.test_attempt == attemptId);
  }, [attemptId, revisionData]);

  // Flatten questions into grading rows: main question rows + sub-question rows (1, 1a, 1b, 1c, 2, ...)
  // Order by question id to match PDF worksheet and Question Details table
  const gradingRows = useMemo(() => {
    if (!Array.isArray(questions) || questions.length === 0) return [];
    const rows = [];
    const orderedQuestions = [...questions].sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
    
    // Iterate through questions in the correct sequence order
    orderedQuestions.forEach((q, idx) => {
      const qNo = idx + 1;
      const hasSub = q.has_subquestions && Array.isArray(q.sub_questions) && q.sub_questions.length > 0;
      if (hasSub) {
        // Sort sub-questions by order field to maintain sequence (a, b, c, etc.)
        const sortedSubQuestions = [...q.sub_questions].sort((a, b) => {
          const orderA = a.order ?? a.order_number ?? 0;
          const orderB = b.order ?? b.order_number ?? 0;
          if (orderA !== orderB) return orderA - orderB;
          // Fallback to id if order is same
          return (a.id ?? 0) - (b.id ?? 0);
        });
        
        sortedSubQuestions.forEach((sq) => {
          const subAns = (q.sub_answers || []).find((sa) => String(sa.sub_question_id ?? sa.sub_question) === String(sq.id));
          rows.push({
            questionNo: `${qNo}${sq.letter || ''}`,
            id: sq.id,
            correct_answer: sq.correct_answer ?? '',
            user_answer: subAns?.answer ?? '',
            isSub: true,
            mainQuestionId: q.id,
            subStudentAnswerId: subAns?.id ?? null,
            marks_obtained: subAns?.marks_obtained ?? null,
            mark_percentage_sub: subAns?.mark_percentage ?? null,
            rubric_notation: subAns?.rubric_notation ?? null,
            rubric_steps: subAns?.rubric_steps ?? null,
            question: { 
              ...q, 
              id: sq.id, 
              correct_answer: sq.correct_answer ?? '', 
              options: sq.options ?? q.options ?? [],
              question_type: sq.question_type ?? sq.type ?? q.question_type ?? q.type ?? '',
            },
            created_by: sq.created_by ?? q.created_by,
          });
        });
      } else {
        rows.push({
          questionNo: String(qNo),
          id: q.id,
          correct_answer: q.correct_answer ?? '',
          user_answer: q.user_answer ?? '',
          isSub: false,
          mainQuestionId: q.id,
          subStudentAnswerId: null,
          mark_percentage_sub: null,
          rubric_notation: null,
          rubric_steps: null,
          question: q,
          created_by: q.created_by,
        });
      }
    });
    return rows;
  }, [questions]);

  const gradingRowKey = useCallback((row) => (row.isSub ? `sub-${row.mainQuestionId}-${row.id}` : String(row.id)), []);

  const gradingProgress = useMemo(() => {
    const total = gradingRows.length;
    if (total === 0) return { graded: 0, total: 0, pct: 0 };
    let graded = 0;
    gradingRows.forEach((row) => {
      if (isGradingRowMarked(row, questions, rubricOverrides)) graded += 1;
    });
    return { graded, total, pct: Math.round((graded / total) * 100) };
  }, [gradingRows, questions, rubricOverrides]);

  const [activeGradeRowKey, setActiveGradeRowKey] = useState(null);

  useEffect(() => {
    if (gradingRows.length === 0) {
      setActiveGradeRowKey(null);
      return;
    }
    const keys = gradingRows.map((r) => gradingRowKey(r));
    setActiveGradeRowKey((prev) => (prev && keys.includes(prev) ? prev : keys[0]));
  }, [gradingRows, gradingRowKey]);

  const scrollToGradingRow = useCallback(
    (key) => {
      setActiveGradeRowKey(key);
      requestAnimationFrame(() => {
        const el = document.getElementById(`grade-row-${key}`);
        el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    },
    []
  );

  const gradeFieldLabelSx = {
    fontSize: '0.62rem',
    fontWeight: 700,
    letterSpacing: '0.1em',
    color: 'text.secondary',
    textTransform: 'uppercase',
    display: 'block',
    mb: 0.5,
  };

  const handleDragStart = useCallback((e) => {
    if (maximized || resizingHandle) return;
    if (e.target.closest('button') || e.target.closest('[role="button"]')) return;
    e.preventDefault();
    dragStartRef.current = {
      clientX: e.clientX,
      clientY: e.clientY,
      offsetX: dragOffsetRef.current.x,
      offsetY: dragOffsetRef.current.y,
    };
    const onMove = (e) => {
      const x = dragStartRef.current.offsetX + (e.clientX - dragStartRef.current.clientX);
      const y = dragStartRef.current.offsetY + (e.clientY - dragStartRef.current.clientY);
      dragOffsetRef.current = { x, y };
      if (dialogPaperRef.current) {
        dialogPaperRef.current.style.transform = `translate(${x}px, ${y}px)`;
      }
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'move';
    document.body.style.userSelect = 'none';
  }, [maximized, resizingHandle]);

  useEffect(() => {
    if (!open) {
      dragOffsetRef.current = { x: 0, y: 0 };
      if (dialogPaperRef.current) dialogPaperRef.current.style.transform = '';
      setMobileGradeDialogRow(null);
      setTextInputAnchor(null);
    }
  }, [open]);

  useEffect(() => {
    if (textInputAnchor) setTextInputValue('');
  }, [textInputAnchor]);

  const resizeHandles = !maximized && !isMobile ? [
    { pos: 'n', cursor: 'n-resize', style: { top: 0, left: RESIZE_HANDLE_SIZE, right: RESIZE_HANDLE_SIZE, height: RESIZE_HANDLE_SIZE } },
    { pos: 's', cursor: 's-resize', style: { bottom: 0, left: RESIZE_HANDLE_SIZE, right: RESIZE_HANDLE_SIZE, height: RESIZE_HANDLE_SIZE } },
    { pos: 'e', cursor: 'e-resize', style: { top: RESIZE_HANDLE_SIZE, right: 0, bottom: RESIZE_HANDLE_SIZE, width: RESIZE_HANDLE_SIZE } },
    { pos: 'w', cursor: 'w-resize', style: { top: RESIZE_HANDLE_SIZE, left: 0, bottom: RESIZE_HANDLE_SIZE, width: RESIZE_HANDLE_SIZE } },
    { pos: 'ne', cursor: 'ne-resize', style: { top: 0, right: 0, width: RESIZE_HANDLE_SIZE, height: RESIZE_HANDLE_SIZE } },
    { pos: 'nw', cursor: 'nw-resize', style: { top: 0, left: 0, width: RESIZE_HANDLE_SIZE, height: RESIZE_HANDLE_SIZE } },
    { pos: 'se', cursor: 'se-resize', style: { bottom: 0, right: 0, width: RESIZE_HANDLE_SIZE, height: RESIZE_HANDLE_SIZE } },
    { pos: 'sw', cursor: 'sw-resize', style: { bottom: 0, left: 0, width: RESIZE_HANDLE_SIZE, height: RESIZE_HANDLE_SIZE } },
  ] : [];

  return (
    <Dialog
      open={open}
      onClose={handleRequestClose}
      maxWidth={false}
      fullWidth={maximized || isMobile}
      fullScreen={maximized || isMobile}
      PaperProps={{
        ref: dialogPaperRef,
        sx: (maximized || isMobile)
          ? {}
          : {
              width: dialogSize.width,
              height: dialogSize.height,
              maxWidth: 'none',
              position: 'relative',
              overflow: 'visible',
              display: 'flex',
              flexDirection: 'column',
            },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: (maximized || isMobile) ? 'default' : 'move',
          py: { xs: 1.25, md: 2 },
        }}
        onMouseDown={!maximized && !isMobile ? handleDragStart : undefined}
        onDoubleClick={() => setMaximized((m) => !m)}
      >
        <Typography variant="h6" sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>{filename || 'PDF Annotator'}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton onClick={() => setMaximized((m) => !m)} size="small" aria-label={maximized ? 'Restore' : 'Maximize'}>
            {maximized ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
          <IconButton onClick={() => void handleRequestClose()} size="small" aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ p: maximized || isMobile ? 0.5 : 1, flex: 1, minHeight: 200, display: 'flex', flexDirection: showQuestionsPanel && !isMobile ? 'row' : 'column', overflow: 'hidden' }}>
        {/* Left: PDF viewer */}
        <Box ref={pdfContainerRef} sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
              <CircularProgress />
            </Box>
          ) : pdfData ? (
            <>
              {canAnnotate && (
                <>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
                    <FormControl size="small" sx={{ minWidth: { xs: 120, md: 160 } }}>
                      <InputLabel>Show annotations</InputLabel>
                      <Select
                        value={annotationFilter}
                        label="Show annotations"
                        onChange={(e) => setAnnotationFilter(e.target.value)}
                      >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="student">Student</MenuItem>
                        <MenuItem value="teacher">Teacher</MenuItem>
                      </Select>
                    </FormControl>
                    <ToggleButtonGroup value={tool} exclusive onChange={(_, v) => v && setTool(v)} size="small">
                      <ToggleButton value={TOOLS.pen}>
                        <PenIcon sx={{ mr: 0.5 }} /> Pen
                      </ToggleButton>
                      <ToggleButton value={TOOLS.highlighter}>
                        <HighlighterIcon sx={{ mr: 0.5 }} /> Highlighter
                      </ToggleButton>
                      <ToggleButton value={TOOLS.eraser}>
                        <EraserIcon sx={{ mr: 0.5 }} /> Erase
                      </ToggleButton>
                      <ToggleButton value={TOOLS.text}>
                        <TextIcon sx={{ mr: 0.5 }} /> Text
                      </ToggleButton>
                    </ToggleButtonGroup>
                    {(tool === TOOLS.pen || tool === TOOLS.text || tool === TOOLS.highlighter) && (
                    <Stack direction="row" spacing={0.5}>
                      {(tool === TOOLS.highlighter ? HIGHLIGHTER_COLORS : PEN_COLORS).map((c) => (
                        <Box
                          key={c}
                          onClick={() => setColor(c)}
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            bgcolor: c,
                            border: color === c ? 2 : 1,
                            borderColor: color === c ? 'primary.main' : 'divider',
                            cursor: 'pointer',
                          }}
                        />
                      ))}
                    </Stack>
                    )}
                    <AiReviewButton
                      enabled={aiReview.featureEnabled}
                      loading={aiReview.loadingConfig}
                      reviewing={aiReview.reviewing}
                      disabledReason={aiReview.configError}
                      onClick={aiReview.runReview}
                    />
                    {saving && <Typography variant="caption" color="text.secondary">Saving...</Typography>}
                  </Stack>
                  <AiReviewPanel
                    summary={aiReview.summary}
                    error={aiReview.error}
                    count={aiReview.recommendations.length}
                    reviewing={aiReview.reviewing}
                    onApply={applyAiSuggestions}
                    onClear={aiReview.clearRecommendations}
                  />
                </>
              )}
              <Box
                ref={scrollContainerRef}
                sx={{
                  overflowX: 'auto',
                  overflowY: isMobile ? 'scroll' : 'auto',
                  flex: 1,
                  minHeight: 0,
                  maxHeight: maximized ? 'calc(100vh - 200px)' : undefined,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  alignSelf: 'flex-start',
                  width: 'fit-content',
                  minWidth: 0,
                  maxWidth: '100%',
                  WebkitOverflowScrolling: 'touch',
                  touchAction: 'pan-x pan-y',
                  ...(isMobile && {
                    '&::-webkit-scrollbar': { width: 12, height: 12 },
                    '&::-webkit-scrollbar-track': { bgcolor: 'action.hover', borderRadius: 6 },
                    '&::-webkit-scrollbar-thumb': { bgcolor: 'primary.main', borderRadius: 6, minHeight: 48, minWidth: 48 },
                    '&::-webkit-scrollbar-thumb:hover': { bgcolor: 'primary.dark' },
                  }),
                }}
              >
                <Box sx={{ width: 'fit-content', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Document file={documentFile} onLoadSuccess={handleDocumentLoadSuccess}>
                    {Array.from({ length: numPages }, (_, i) => renderPageWithOverlay(i))}
                  </Document>
                </Box>
              </Box>
            </>
          ) : (
            <Typography color="text.secondary">Unable to load PDF</Typography>
          )}
        </Box>

        {/* Right (or below on mobile): Question details with Note and Mark */}
        {showQuestionsPanel && (
          <Box
            sx={{
              width: isMobile ? '100%' : 440,
              minWidth: isMobile ? 0 : 400,
              borderLeft: isMobile ? 0 : 1,
              borderTop: isMobile ? 1 : 0,
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              maxHeight: isMobile ? '28vh' : undefined,
              flexShrink: isMobile ? 0 : undefined,
              bgcolor: isMobile ? 'background.paper' : alpha('#7c3aed', 0.06),
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                px: 2,
                py: 1.25,
                fontWeight: 800,
                borderBottom: 1,
                borderColor: 'divider',
                fontSize: { xs: '0.8rem', md: '1rem' },
                color: 'text.primary',
                bgcolor: isMobile ? 'transparent' : alpha('#fff', 0.65),
              }}
            >
              {readOnlyAnnotations && !isTeacher ? 'Graded by Teacher' : 'Grading panel'}
            </Typography>
            {!isMobile && (
              <Box
                sx={{
                  px: 2,
                  pt: 1.25,
                  pb: 1.5,
                  borderBottom: 1,
                  borderColor: 'divider',
                  bgcolor: alpha('#fff', 0.72),
                  flexShrink: 0,
                }}
              >
                <Typography sx={{ ...gradeFieldLabelSx, mb: 0.75 }}>Questions</Typography>
                <Stack
                  direction="row"
                  spacing={0.75}
                  sx={{
                    overflowX: 'auto',
                    flexWrap: 'nowrap',
                    pb: 0.25,
                    '&::-webkit-scrollbar': { height: 6 },
                    '&::-webkit-scrollbar-thumb': { borderRadius: 3, bgcolor: 'action.disabled' },
                  }}
                >
                  {gradingRows.map((row) => {
                    const key = gradingRowKey(row);
                    const active = activeGradeRowKey === key;
                    const marked = isGradingRowMarked(row, questions, rubricOverrides);
                    return (
                      <Box
                        key={key}
                        component="button"
                        type="button"
                        onClick={() => scrollToGradingRow(key)}
                        sx={{
                          minWidth: 32,
                          height: 30,
                          px: 0.85,
                          border: !marked && !active ? '1px solid' : 'none',
                          borderColor: alpha(theme.palette.grey[500], 0.35),
                          borderRadius: 1,
                          bgcolor: marked ? 'success.main' : active ? 'primary.main' : alpha('#fff', 0.95),
                          color: marked
                            ? theme.palette.success.contrastText
                            : active
                              ? theme.palette.primary.contrastText
                              : 'text.secondary',
                          boxShadow: active && marked ? `0 0 0 2px ${theme.palette.primary.main}` : 'none',
                          fontSize: '0.72rem',
                          fontWeight: 800,
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                          lineHeight: 1,
                          flexShrink: 0,
                        }}
                      >
                        {row.questionNo}
                      </Box>
                    );
                  })}
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.25, fontSize: '0.75rem' }}>
                  {gradingProgress.graded} / {gradingProgress.total} questions graded
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={gradingProgress.pct}
                  sx={{
                    mt: 0.75,
                    height: 8,
                    borderRadius: 999,
                    bgcolor: alpha('#7c3aed', 0.14),
                    '& .MuiLinearProgress-bar': { borderRadius: 999 },
                  }}
                />
              </Box>
            )}
            {isMobile ? (
              <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Typography variant="caption" color="text.secondary" sx={{ px: 1.5, pt: 0.5, fontSize: '0.7rem' }}>Tap a question to grade</Typography>
                <Box
                  sx={{
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    flexShrink: 0,
                    py: 1,
                    px: 1,
                    display: 'flex',
                    gap: 0.75,
                    alignItems: 'center',
                    minHeight: 0,
                    '&::-webkit-scrollbar': { height: 6 },
                    '&::-webkit-scrollbar-thumb': { borderRadius: 3, bgcolor: 'action.disabled' },
                  }}
                >
                  {gradingRows.map((row) => {
                    const key = gradingRowKey(row);
                    const selected = mobileGradeDialogRow && gradingRowKey(mobileGradeDialogRow) === key;
                    const marked = isGradingRowMarked(row, questions, rubricOverrides);
                    return (
                      <Chip
                        key={key}
                        label={`Q${row.questionNo}`}
                        onClick={() => setMobileGradeDialogRow(row)}
                        variant={selected || marked ? 'filled' : 'outlined'}
                        color={marked ? 'success' : selected ? 'primary' : 'default'}
                        size="medium"
                        sx={{
                          flexShrink: 0,
                          fontWeight: 600,
                          fontSize: '0.8rem',
                          ...(selected && marked
                            ? { boxShadow: `0 0 0 2px ${theme.palette.primary.main}` }
                            : {}),
                        }}
                      />
                    );
                  })}
                </Box>
                <Dialog
                  open={Boolean(mobileGradeDialogRow)}
                  onClose={() => void closeMobileGradeDialog()}
                  fullScreen
                  PaperProps={{ sx: { maxHeight: '90vh', borderRadius: 2 } }}
                >
                  {mobileGradeDialogRow && (
                    <>
                      <DialogTitle sx={{ py: 1.5, px: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="subtitle1" fontWeight={600}>Grade Q{mobileGradeDialogRow.questionNo}</Typography>
                        <IconButton size="small" onClick={() => void closeMobileGradeDialog()} aria-label="Close">
                          <CloseIcon />
                        </IconButton>
                      </DialogTitle>
                      <DialogContent sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 700 }}>
                              Student&apos;s response
                            </Typography>
                            <MathRenderer content={mobileGradeDialogRow.user_answer != null && mobileGradeDialogRow.user_answer !== '' ? String(mobileGradeDialogRow.user_answer) : '—'} variant="body2" />
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 700 }}>
                              Correct answer
                            </Typography>
                            <CorrectAnswerEditor
                              questionId={mobileGradeDialogRow.id}
                              correctAnswer={mobileGradeDialogRow.correct_answer}
                              isEditable={isTeacher && mobileGradeDialogRow.created_by && currentUserId && parseInt(mobileGradeDialogRow.created_by) === parseInt(currentUserId) && !readOnlyAnnotations}
                              question={mobileGradeDialogRow.question}
                              placeholder="Correct Answer"
                            />
                          </Box>
                        </Stack>
                        <Box>
                          <Typography sx={{ ...gradeFieldLabelSx, mt: 0 }}>Rubric</Typography>
                          {!mobileGradeDialogRow.isSub ? (
                            <RubricTripleEditor
                              attemptId={attemptId}
                              questionId={mobileGradeDialogRow.id}
                              registerDeferredSave={deferredRubricSave}
                              onRubricPersisted={handleRubricPersisted}
                            />
                          ) : mobileGradeDialogRow.subStudentAnswerId ? (
                            <RubricTripleEditor
                              attemptId={attemptId}
                              questionId={mobileGradeDialogRow.mainQuestionId}
                              subAnswerId={mobileGradeDialogRow.subStudentAnswerId}
                              registerDeferredSave={deferredRubricSave}
                              onRubricPersisted={handleRubricPersisted}
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Sub-answer record missing — save the attempt and reopen to grade this part.
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ pt: 1, borderTop: 1, borderColor: 'divider' }}>
                          <Typography sx={{ ...gradeFieldLabelSx, mt: 0 }}>Note</Typography>
                          <TeacherNoteEditor
                            attemptId={attemptId}
                            questionId={mobileGradeDialogRow.isSub ? mobileGradeDialogRow.mainQuestionId : mobileGradeDialogRow.id}
                            subQuestionId={mobileGradeDialogRow.isSub ? mobileGradeDialogRow.id : null}
                            revisionData={filteredRevisionData}
                          />
                        </Box>
                      </DialogContent>
                      <DialogActions sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: 'divider' }}>
                        <Button variant="contained" onClick={() => void closeMobileGradeDialog()}>Done</Button>
                      </DialogActions>
                    </>
                  )}
                </Dialog>
              </Box>
            ) : (
              <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 2, py: 1.5 }}>
                <Stack spacing={1.5}>
                  {gradingRows.map((row) => {
                    const key = gradingRowKey(row);
                    const selected = activeGradeRowKey === key;
                    return (
                      <Box
                        key={key}
                        id={`grade-row-${key}`}
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: alpha('#fff', 0.94),
                          border: '1px solid',
                          borderColor: selected ? 'primary.main' : alpha(theme.palette.grey[400], 0.35),
                          boxShadow: selected ? `0 0 0 2px ${alpha(theme.palette.primary.main, 0.35)}` : '0 2px 8px rgba(15, 23, 42, 0.04)',
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1 }}>
                          <Box
                            sx={{
                              minWidth: 36,
                              height: 36,
                              borderRadius: 1,
                              bgcolor: 'primary.main',
                              color: 'primary.contrastText',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 800,
                              fontSize: '0.78rem',
                              flexShrink: 0,
                            }}
                          >
                            {row.questionNo}
                          </Box>
                          <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color: 'text.primary' }}>
                            Question {row.questionNo}
                          </Typography>
                        </Stack>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems="flex-start">
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ ...gradeFieldLabelSx, mt: 0 }}>Student&apos;s response</Typography>
                            <MathRenderer
                              content={row.user_answer != null && row.user_answer !== '' ? String(row.user_answer) : '—'}
                              variant="body2"
                              sx={{ whiteSpace: 'pre-wrap', color: 'text.primary' }}
                            />
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ ...gradeFieldLabelSx, mt: 0 }}>Correct answer</Typography>
                            <CorrectAnswerEditor
                              questionId={row.id}
                              correctAnswer={row.correct_answer}
                              isEditable={isTeacher && row.created_by && currentUserId && parseInt(row.created_by) === parseInt(currentUserId) && !readOnlyAnnotations}
                              question={row.question}
                              placeholder="Add"
                              compact
                            />
                          </Box>
                        </Stack>
                        <Box sx={{ mt: 1.25 }}>
                          <Typography sx={gradeFieldLabelSx}>Rubric</Typography>
                          {!row.isSub ? (
                            <RubricTripleEditor
                              attemptId={attemptId}
                              questionId={row.id}
                              registerDeferredSave={deferredRubricSave}
                              onRubricPersisted={handleRubricPersisted}
                            />
                          ) : row.subStudentAnswerId ? (
                            <RubricTripleEditor
                              attemptId={attemptId}
                              questionId={row.mainQuestionId}
                              subAnswerId={row.subStudentAnswerId}
                              registerDeferredSave={deferredRubricSave}
                              onRubricPersisted={handleRubricPersisted}
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Sub-answer record missing — save the attempt and reopen.
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ mt: 1.25 }}>
                          <Typography sx={gradeFieldLabelSx}>Note</Typography>
                          <TeacherNoteEditor
                            attemptId={attemptId}
                            questionId={row.isSub ? row.mainQuestionId : row.id}
                            subQuestionId={row.isSub ? row.id : null}
                            revisionData={filteredRevisionData}
                          />
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => void handleRequestClose()}>Save and Close</Button>
        {(isTeacher || role === 'student') && (
          <Button
              variant="contained"
              onClick={handleExport}
              disabled={exporting || !pdfData}
              startIcon={exporting ? <CircularProgress size={16} /> : <ExportIcon />}
            >
              Export PDF
            </Button>
        )}
      </DialogActions>
      {resizeHandles?.map((h) => (
        <Box
          key={h.pos}
          component="span"
          role="presentation"
          onMouseDown={(e) => handleResizeStart(h.pos, e)}
          sx={{
            position: 'absolute',
            cursor: h.cursor,
            zIndex: 1300,
            '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' },
            ...h.style,
          }}
        />
      ))}
      <Dialog open={Boolean(textInputAnchor)} onClose={() => setTextInputAnchor(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Add text</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Text"
            placeholder="Type to add on PDF..."
            value={textInputValue}
            onChange={(e) => setTextInputValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (textInputAnchor && textInputValue.trim()) {
                  addTextToPage(textInputAnchor.pageIndex, {
                    x: textInputAnchor.x,
                    y: textInputAnchor.y,
                    text: textInputValue.trim(),
                    color,
                    fontSize: 0.025,
                    role,
                  });
                  setTextInputAnchor(null);
                }
              }
            }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTextInputAnchor(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (textInputAnchor && textInputValue.trim()) {
                addTextToPage(textInputAnchor.pageIndex, {
                  x: textInputAnchor.x,
                  y: textInputAnchor.y,
                  text: textInputValue.trim(),
                  color,
                  fontSize: 0.025,
                  role,
                });
                setTextInputAnchor(null);
              }
            }}
            disabled={!textInputValue.trim()}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Dialog>
  );
};

export default PdfAnnotationViewer;
